import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { 
  signOut, 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  updatePassword,
  PhoneAuthProvider, 
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { auth } from '../../../../firebase';
import PasswordRequirements from '../../../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../../../hooks/auth/usePasswordValidation';
import InfoTooltip from '../../../../components/InfoTooltip';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import MfaDialog from '../../../../components/auth/MfaDialog';
import SwitchToTotpDialog from '../../../../components/auth/SwitchToTotpDialog';
import useAccessibilityPrefs from '../../../../hooks/useAccessibilityPrefs';
import { useAuth } from '../../../../hooks/useAuth';
import { getServiceUserProfileDocRef } from '../../../../utils/informantFirestorePaths';
import {
  buildMfaResolver,
  determineMfaType,
  getPhoneHint,
  getTotpHint,
  sendMfaCode,
  buildTotpAssertion,
} from '../../../../hooks/auth/mfaUtils';
// Reusable toggle switch for the accessibility settings tab.
function A11yToggle({ id, label, description, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="flex-1 min-w-0">
        <label htmlFor={id} className="text-sm font-semibold text-gray-800 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <button
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent cursor-pointer transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
          checked ? 'bg-violet-600' : 'bg-gray-300'
        }`}
      >
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

// Segmented selector for text spacing (3 options).
function A11ySegmented({ id, label, description, value, options, onChange }) {
  return (
    <div className="py-3">
      <div className="mb-2">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <div
        id={id}
        role="radiogroup"
        aria-label={label}
        className="inline-flex rounded-lg border border-gray-300 overflow-hidden"
      >
        {options.map((opt) => (
          <button
            key={opt.value}
            role="radio"
            aria-checked={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-4 py-1.5 text-xs font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-inset ${
              value === opt.value
                ? 'bg-violet-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PatientSettings({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { prefs, updatePref, resetPrefs } = useAccessibilityPrefs();
  // Custom claims (userType / assessmentModule) tell us which Firestore
  // collection this service user's profile lives in, so MFA metadata is written
  // to the right document (patients/{uid} vs informants/{uid}).
  const { claims } = useAuth();

  // Feature flag — set to true when accessibility tab is ready for production.
  const SHOW_A11Y_TAB = false;
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(null);

  // Error code mapping for Settings password change (Patient)
  // PS-001W: Current password incorrect
  // PS-001R: Requires recent login
  // PS-001S: New password same as current (no-op change)
  // PS-001P: Password policy/validation failure
  // PS-001M: New passwords do not match
  // PS-001V: Invalid MFA verification code
  // PS-001G: Generic error

  // Tab state
  const [activeTab, setActiveTab] = useState('password');
  
  
  // MFA states
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [resolver, setResolver] = useState(null);
  const [mfaType, setMfaType] = useState('phone');
  const recaptchaVerifierRef = useRef(null);
  const [sentAt, setSentAt] = useState(0);
  // Dedicated MFA loading to avoid blocking dialog actions with form loading
  const [mfaBusy, setMfaBusy] = useState(false);

  // ---------------------------------------------------------------------------
  // Two-Factor Authentication tab state
  // ---------------------------------------------------------------------------
  // Unlike providers (where MFA is mandatory), service-user MFA is OPTIONAL, so
  // the tab has three states:
  //   'none'  — no factor enrolled: prompt the user to set one up.
  //   'phone' — SMS enrolled: offer an in-app switch to an authenticator app.
  //   'totp'  — authenticator app enrolled: nothing further to do.
  const [currentMfaMethod, setCurrentMfaMethod] = useState('none'); // 'none' | 'phone' | 'totp'
  const [currentMfaPhoneNumber, setCurrentMfaPhoneNumber] = useState(''); // masked, SMS only
  const [currentMfaFactorUid, setCurrentMfaFactorUid] = useState(null); // needed to unenroll SMS on switch
  const [showSwitchMfaDialog, setShowSwitchMfaDialog] = useState(false);

  // Reset all form and UI state each time the dialog opens so the user always
  // starts with a clean form. Without this, closing mid-flow (e.g. after an
  // error or partial input) and reopening would expose stale state.
  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setErrorCode('');
      setSuccess(false);
      setIsLoading(false);
      setShowCurrentPassword(false);
      setShowNewPassword(true);
      setShowConfirmPassword(true);
      setIsPasswordValid(false);
      setPasswordMatch(null);
      setActiveTab('password');
      setShowMFAPrompt(false);
      setVerificationId(null);
      setResolver(null);
      setMfaType('phone');
      setSentAt(0);
      setMfaBusy(false);
      setShowSwitchMfaDialog(false);
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Detect the user's currently enrolled MFA factor whenever the dialog opens.
  // We inspect Firebase Auth's multiFactor(user).enrolledFactors to work out
  // whether they have no factor, SMS, or an authenticator app — and capture the
  // factor UID (needed to unenroll SMS during a switch) and a masked phone
  // number for display. Mirrors the provider ProviderSettings implementation.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setCurrentMfaMethod('none');
      setCurrentMfaFactorUid(null);
      setCurrentMfaPhoneNumber('');
      return;
    }
    try {
      const enrolled = multiFactor(currentUser).enrolledFactors || [];
      if (enrolled.length === 0) {
        setCurrentMfaMethod('none');
        setCurrentMfaFactorUid(null);
        setCurrentMfaPhoneNumber('');
        return;
      }
      const factor = enrolled[0]; // We support one factor at a time
      if (factor.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
        setCurrentMfaMethod('totp');
        setCurrentMfaPhoneNumber('');
      } else {
        setCurrentMfaMethod('phone');
        const phone = factor.phoneNumber || '';
        const last4 = phone.replace(/\D/g, '').slice(-4);
        setCurrentMfaPhoneNumber(last4 ? `••••${last4}` : '');
      }
      setCurrentMfaFactorUid(factor.uid);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[PatientSettings] Error reading MFA factors:', err);
      }
      setCurrentMfaMethod('none');
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // Handle a successful SMS -> authenticator switch (callback from
  // SwitchToTotpDialog). Updates local display state so the tab immediately
  // reflects the new method without a reload.
  // ---------------------------------------------------------------------------
  const handleSwitchMfaSuccess = () => {
    setCurrentMfaMethod('totp');
    setCurrentMfaPhoneNumber('');
    try {
      const user = auth.currentUser;
      if (user) {
        const enrolled = multiFactor(user).enrolledFactors || [];
        const totpFactor = enrolled.find(
          (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
        );
        if (totpFactor) setCurrentMfaFactorUid(totpFactor.uid);
      }
    } catch (_) {}
  };

  // Send the user to the dedicated setup page to enroll a first factor. We close
  // the settings dialog first so the navigation is not obscured by the modal.
  const handleGoToMfaSetup = () => {
    onClose();
    navigate('/service-user/set-up-mfa');
  };

  const handleNewPasswordChange = (e) => {
    const newPass = e.target.value;
    setNewPassword(newPass);
    
    if (confirmPassword) {
      setPasswordMatch(confirmPassword === newPass);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPass = e.target.value;
    setConfirmPassword(confirmPass);
    setPasswordMatch(confirmPass === newPassword);
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setSuccess(false);
    setIsLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in first');

      const { errors } = validatePasswordString(newPassword);
      if (errors.length > 0) {
        // Consolidate client-side validation into banner + code
        setError(errors.join('. '));
        setErrorCode('PS-001P');
        setIsLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        setErrorCode('PS-001M');
        setIsLoading(false);
        return;
      }

      // Check if new password is same as current password
      if (newPassword === currentPassword) {
        setError('New password must be different from your current password');
        setErrorCode('PS-001S');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordMatch(null);
        return;
      }

      // Create credential
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      try {
        // Attempt to reauthenticate
        await reauthenticateWithCredential(user, credential);
        
        // If successful (no MFA required), update password
        await updatePassword(user, newPassword);
        handlePasswordUpdateSuccess();

      } catch (err) {
        // Handle MFA required error
        if (err.code === 'auth/multi-factor-auth-required') {
          try {
            const mfaResolver = buildMfaResolver(auth, err);
            const detectedMfaType = determineMfaType(mfaResolver);

            if (detectedMfaType === 'totp') {
              // TOTP path: no reCAPTCHA or SMS needed
              setResolver(mfaResolver);
              setMfaType('totp');
              setIsLoading(false);
              setShowMFAPrompt(true);
            } else {
              // Phone/SMS path: send verification code via reCAPTCHA
              if (!recaptchaVerifierRef.current) {
                recaptchaVerifierRef.current = new RecaptchaVerifier(auth,
                  'recaptcha-container',
                  { size: 'invisible', callback: () => {} }
                );
              }

              const { verificationId: vId } = await sendMfaCode(
                auth, mfaResolver, recaptchaVerifierRef.current
              );

              setVerificationId(vId);
              setResolver(mfaResolver);
              setMfaType('phone');
              setSentAt(Date.now());
              setIsLoading(false);
              setShowMFAPrompt(true);
            }
            return;

          } catch (mfaError) {
            console.error('Error handling MFA:', mfaError);
            throw new Error('Error setting up MFA verification');
          }
        } else if (err.code === 'auth/wrong-password') {
          // Consolidated banner + code only; no per-field hint
          setError('Current password is incorrect');
          setErrorCode('PS-001W');
          setCurrentPassword(''); // Clear the current password field
        } else {
          throw err;
        }
      }

    } catch (err) {
      console.error('Change Password Error:', err);
      switch (err.code) {
        case 'auth/wrong-password':
          setError('Current password is incorrect');
          setErrorCode('PS-001W');
          setCurrentPassword('');
          break;
        case 'auth/requires-recent-login':
          setError('Please sign in again before changing your password');
          setErrorCode('PS-001R');
          await signOut(auth);
          navigate('/service-user/login');
          break;
        default:
          setError(err.message || 'Error changing password');
          setErrorCode('PS-001G');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // handleVerifyMFA is now performed via shared MfaDialog onVerify

  const handlePasswordUpdateSuccess = () => {
    // Clear form
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    
    setSuccess(true);
    
    // Sign out user after successful password change
    setTimeout(async () => {
      await signOut(auth);
      navigate('/service-user/login');
    }, 2000);
  };

  return (
    <>
      <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[90vw] max-w-3xl max-h-[85vh] overflow-y-auto z-50">
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('password')}
                className={`py-3 px-4 text-sm font-medium ${
                  activeTab === 'password' 
                  ? 'text-violet-900 border-b-2 border-violet-900' 
                  : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Change Password
              </button>
              <button
                onClick={() => setActiveTab('twofa')}
                className={`py-3 px-4 text-sm font-medium ${
                  activeTab === 'twofa'
                  ? 'text-violet-900 border-b-2 border-violet-900'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Two-Factor Authentication
              </button>
              {SHOW_A11Y_TAB && (
                <button
                  onClick={() => setActiveTab('accessibility')}
                  className={`py-3 px-4 text-sm font-medium ${
                    activeTab === 'accessibility'
                    ? 'text-violet-900 border-b-2 border-violet-900'
                    : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Accessibility
                </button>
              )}
            </div>

            {/* Password Change Tab */}
            {activeTab === 'password' && (
              <>
                <AlertDialog.Title className="text-xl font-bold mb-4">
                  Change Password
                </AlertDialog.Title>

                {success ? (
                  <div className="text-green-600 mb-4">
                    Password changed successfully! You will be signed out in a moment...
                  </div>
                ) : (
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    {/* Current Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          value={currentPassword}
                          onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            setError('');
                            setErrorCode('');
                          }}
                          required
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                        >
                          <span className="relative h-5 w-5">
                            <EyeIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showCurrentPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                              }`}
                            />
                            <EyeOffIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showCurrentPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                              }`}
                            />
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* New Password Field */}
                    <div>
                      <div className="flex justify-between items-center">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <InfoTooltip
                          side="right"
                          content={"Your password needs to have 12 characters or more, and contain at least 1 number, 1 symbol and 1 upper & lower case letter"}
                        />
                      </div>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          required
                          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                        >
                          <span className="relative h-5 w-5">
                            <EyeIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showNewPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                              }`}
                            />
                            <EyeOffIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showNewPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                              }`}
                            />
                          </span>
                        </button>
                      </div>
                      <PasswordRequirements value={newPassword} onValidityChange={setIsPasswordValid} className="mt-2" />
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          required
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          <span className="relative h-5 w-5">
                            <EyeIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showConfirmPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                              }`}
                            />
                            <EyeOffIcon
                              className={`absolute inset-0 h-5 w-5 text-gray-400 transition-all duration-150 ${
                                showConfirmPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                              }`}
                            />
                          </span>
                        </button>
                      </div>
                      {passwordMatch !== null && (
                        <div className={`mt-1 overflow-hidden transition-all duration-300 ${passwordMatch !== null ? 'max-h-6' : 'max-h-0'}`}>
                          <div className="flex items-start text-sm">
                            <span className={`mr-2 font-bold ${passwordMatch ? 'text-green-600' : 'text-gray-500'}`} aria-hidden>
                              {passwordMatch ? '✔' : '✘'}
                            </span>
                            <span className={`${passwordMatch ? 'text-green-700' : 'text-gray-700'}`}>
                              {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Consolidated error banner below inputs and above CTA */}
                    <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mt-2 mb-2' : 'max-h-0 mt-0 mb-0'}`} role="alert">
                      <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
                        <span className="block sm:inline">{error}{errorCode ? ` (Code: ${errorCode})` : ''}</span>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <AlertDialog.Cancel asChild>
                        <button
                          type="button"
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                      </AlertDialog.Cancel>
                      <button
                        type="submit"
                        disabled={isLoading || !isPasswordValid || !passwordMatch}
                        className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500"
                      >
                        {isLoading ? 'Updating...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

            {/* Two-Factor Authentication Tab */}
            {activeTab === 'twofa' && (
              <>
                <AlertDialog.Title className="text-xl font-bold mb-1">
                  Two-Factor Authentication
                </AlertDialog.Title>
                <p className="text-sm text-gray-500 mb-4">
                  Add an extra layer of security to your account. This is optional but recommended.
                </p>

                {currentMfaMethod === 'none' ? (
                  /* No factor enrolled — invite the user to set one up */
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700 mb-4">
                      You have not set up two-factor authentication yet. When enabled, you will be
                      asked for a one-time code (by SMS or an authenticator app) each time you sign in.
                    </p>
                    <button
                      type="button"
                      onClick={handleGoToMfaSetup}
                      className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-primary/90"
                    >
                      Set up two-factor authentication
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Current MFA method display */}
                    <div className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        {/* Icon based on method */}
                        {currentMfaMethod === 'totp' ? (
                          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                            </svg>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {currentMfaMethod === 'totp' ? 'Authenticator App' : 'SMS Text Message'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {currentMfaMethod === 'totp'
                              ? 'Codes generated by your authenticator app'
                              : `Verification codes sent to ${currentMfaPhoneNumber || 'your phone'}`
                            }
                          </p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>

                    {/* Switch button — only for SMS users. TOTP-to-SMS switching is
                        not supported in this iteration (matches provider settings). */}
                    {currentMfaMethod === 'phone' && (
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setShowSwitchMfaDialog(true)}
                          className="text-sm font-medium text-primary hover:text-primary/80 underline"
                        >
                          Switch to Authenticator App
                        </button>
                      </div>
                    )}
                  </>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <AlertDialog.Cancel asChild>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Close
                    </button>
                  </AlertDialog.Cancel>
                </div>
              </>
            )}

            {/* Accessibility Tab */}
            {SHOW_A11Y_TAB && activeTab === 'accessibility' && (
              <>
                <AlertDialog.Title className="text-xl font-bold mb-1">
                  Accessibility
                </AlertDialog.Title>
                <p className="text-sm text-gray-500 mb-4">
                  Customise your experience. Changes apply instantly and are saved automatically.
                </p>

                {/* Visual Comfort */}
                <div className="mb-5">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Visual Comfort</h3>
                  <div className="divide-y divide-gray-100">
                    <A11yToggle
                      id="a11y-reduced-motion"
                      label="Reduced Motion"
                      description="Turn off animations, transitions, and confetti effects"
                      checked={prefs.reducedMotion}
                      onChange={(val) => updatePref('reducedMotion', val)}
                    />
                    <A11yToggle
                      id="a11y-focus-ring"
                      label="Enhanced Focus Indicators"
                      description="Show thicker, higher-contrast outlines when navigating with a keyboard"
                      checked={prefs.focusRing}
                      onChange={(val) => updatePref('focusRing', val)}
                    />
                  </div>
                </div>

                {/* Reading Support */}
                <div className="mb-5">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Reading Support</h3>
                  <div className="divide-y divide-gray-100">
                    <A11yToggle
                      id="a11y-dyslexia-font"
                      label="Dyslexia-Friendly Font"
                      description="Switch to Atkinson Hyperlegible, a font designed for improved letter recognition"
                      checked={prefs.dyslexiaFont}
                      onChange={(val) => updatePref('dyslexiaFont', val)}
                    />
                    <A11ySegmented
                      id="a11y-text-spacing"
                      label="Text Spacing"
                      description="Increase the space between letters, words, and lines for easier reading"
                      value={prefs.textSpacing}
                      options={[
                        { value: 'default', label: 'Default' },
                        { value: 'comfortable', label: 'Comfortable' },
                        { value: 'spacious', label: 'Spacious' },
                      ]}
                      onChange={(val) => updatePref('textSpacing', val)}
                    />
                  </div>
                </div>

                {/* Focus and Attention */}
                <div className="mb-5">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Focus &amp; Attention</h3>
                  <div className="divide-y divide-gray-100">
                    <A11yToggle
                      id="a11y-focus-mode"
                      label="Focus Mode"
                      description="When filling in questionnaires, hide the progress bar and answer summary to reduce distractions"
                      checked={prefs.focusMode}
                      onChange={(val) => updatePref('focusMode', val)}
                    />
                  </div>
                </div>

                {/* Footer actions */}
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetPrefs}
                    className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Reset to defaults
                  </button>
                  <AlertDialog.Cancel asChild>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Close
                    </button>
                  </AlertDialog.Cancel>
                </div>
              </>
            )}

          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* MFA Dialog consistent with sign-in */}
      {showMFAPrompt && (
        <MfaDialog
          open={showMFAPrompt}
          mfaType={mfaType}
          onOpenChange={(open) => {
            setShowMFAPrompt(open);
            if (!open && recaptchaVerifierRef.current) {
              try { recaptchaVerifierRef.current.clear(); } catch (_) {}
              recaptchaVerifierRef.current = null;
            }
          }}
          maskedPhone={mfaType === 'phone' ? (() => { const hint = getPhoneHint(resolver); return hint ? `\u2022\u2022\u2022\u2022${(hint.phoneNumber || '').replace(/\D/g, '').slice(-4)}` : ''; })() : ''}
          isLoading={mfaBusy}
          error={error}
          errorCode={errorCode}
          sentAt={sentAt}
          cooldownSeconds={30}
          tooltipSide="right"
          tooltipContent={"Having trouble receiving the code?\n\nDelays can happen due to your phone carrier or network issues.\n\nYou can try:\n\n→ Clearing your browser’s cache and trying again\n\n→ Using a different browser (like Chrome, Firefox, or Edge)\n\n→ Trying to log in using incognito/private browsing mode\n\nIf the problem continues, please contact your healthcare provider for help."}
          onVerify={async (code) => {
            try {
              setMfaBusy(true);
              if (mfaType === 'totp') {
                // TOTP (authenticator app) verification flow
                const totpHint = getTotpHint(resolver);
                const assertion = buildTotpAssertion(totpHint.uid, code);
                await resolver.resolveSignIn(assertion);
              } else {
                // SMS phone verification flow
                const cred = PhoneAuthProvider.credential(verificationId, code);
                const assertion = PhoneMultiFactorGenerator.assertion(cred);
                await resolver.resolveSignIn(assertion);
              }
              const user = auth.currentUser;
              await updatePassword(user, newPassword);
              handlePasswordUpdateSuccess();
            } catch (_) {
              setError('Invalid verification code. Please try again.');
              setErrorCode('PS-001V');
            } finally {
              setMfaBusy(false);
            }
          }}
          onCancel={() => setShowMFAPrompt(false)}
          onResend={mfaType === 'phone' ? async () => {
            try {
              setMfaBusy(true);
              if (!resolver) return { ok: false };
              // Delegate SMS resend to the shared mfaUtils helper
              const { verificationId: newVId } = await sendMfaCode(auth, resolver, recaptchaVerifierRef.current);
              setVerificationId(newVId);
              setSentAt(Date.now());
              return { ok: true };
            } catch (_) {
              return { ok: false };
            } finally {
              setMfaBusy(false);
            }
          } : undefined}
        />
      )}


      {/* Switch-to-authenticator dialog — handles the full re-auth + enroll +
          unenroll flow without signing the user out. profileDocRef points at the
          service user's own profile document (patients/ or informants/) so the
          MFA metadata is written to the correct collection. */}
      <SwitchToTotpDialog
        open={showSwitchMfaDialog}
        onOpenChange={setShowSwitchMfaDialog}
        onSuccess={handleSwitchMfaSuccess}
        currentMfaFactorUid={currentMfaFactorUid}
        profileDocRef={auth.currentUser ? getServiceUserProfileDocRef(auth.currentUser.uid, claims) : null}
      />

      {/* Add recaptcha container */}
      <div id="recaptcha-container"></div>
    </>
  );
} 